import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { useDispatch, useSelector } from "react-redux";
import {
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import logo from "../../resources/Color variable-07.png";
import { auth } from "../../firebase/firebase";
import { registerOwner } from "../../firebase/firebase.user";
import {
  setUserCredetials,
  setUserDetails,
} from "../../store/user/user.reducer";
import usePumble from "../../hooks/pumble/usePumble";

// Move InputField outside component to prevent recreation
const InputField = ({
  label,
  id,
  type = "text",
  value,
  required,
  error,
  success,
  helpText,
  onChange,
  ...props
}) => (
  <div>
    <label
      htmlFor={id}
      className="block text-sm font-semibold text-slate-700 mb-2"
    >
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        className={`block w-full px-4 py-3 rounded-xl border-2 transition-all text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 ${
          error
            ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
            : success
            ? "border-emerald-300 focus:border-emerald-500 focus:ring-emerald-500/20"
            : "border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
        }`}
        {...props}
      />
      {success && (
        <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
      )}
      {error && (
        <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
      )}
    </div>
    {helpText && (
      <p
        className={`mt-1 text-xs ${error ? "text-red-600" : "text-slate-500"}`}
      >
        {helpText}
      </p>
    )}
  </div>
);

const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    lastName: "",
    email: "",
    password: "",
    praxisName: "",
    praxisTel: "+595",
  });

  const [validation, setValidation] = useState({
    email: null,
    password: null,
    phone: null,
  });

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [canCreate, setCanCreate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [referal, setReferal] = useState(null);

  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const { sendRegister } = usePumble();

  const userCredentials = useSelector((state) => state.user?.userCredentials);
  const userDetails = useSelector((state) => state.user?.userDetails);

  // Check for referral ID in URL params
  useEffect(() => {
    if (params?.praxisReferalId !== undefined) {
      setReferal(params?.praxisReferalId);
    }
  }, [params]);

  // Redirect if already logged in
  useEffect(() => {
    if (user && userDetails) {
      navigate("/dashboard");
    }
  }, [user, userDetails, navigate]);

  // Set user credentials when authenticated
  useEffect(() => {
    if (user && userCredentials === null) {
      let deserializedUser = JSON.parse(JSON.stringify(user));
      dispatch(setUserCredetials(deserializedUser));
    }
  }, [user, userCredentials, dispatch]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return null;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    if (!password) return null;
    return password.length >= 6;
  };

  const validatePhone = (phone) => {
    if (!phone) return null;
    const cleanPhone = phone.replace(/\D/g, "");
    return cleanPhone.length >= 10;
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;

    setFormData((prev) => ({ ...prev, [field]: value }));

    if (field === "email") {
      setValidation((prev) => ({ ...prev, email: validateEmail(value) }));
    } else if (field === "password") {
      setValidation((prev) => ({ ...prev, password: validatePassword(value) }));
    } else if (field === "praxisTel") {
      setValidation((prev) => ({ ...prev, phone: validatePhone(value) }));
    }
  };

  useEffect(() => {
    const isComplete =
      formData.name &&
      formData.lastName &&
      formData.email &&
      formData.password &&
      formData.praxisName &&
      formData.praxisTel &&
      validation.email === true &&
      validation.password === true &&
      validation.phone === true;

    setCanCreate(isComplete);
  }, [formData, validation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate || isLoading) return;

    setIsLoading(true);

    const praxisOwner = {
      user: {
        name: formData.name,
        lastname: formData.lastName,
        phone: null,
        praxisPhone: formData.praxisTel,
        email: formData.email,
        praxis: null,
        address: null,
        role: "owner",
        praxisId: null,
      },
      praxis: {
        praxisName: formData.praxisName,
        address: null,
        praxisPhone: formData.praxisTel,
        createdAt: new Date().toISOString(),
        currentLicenseType: "free",
        payments: [
          {
            paid: false,
            membershipStart: new Date().toISOString(),
            amount: 0,
            paymentDate: null,
            licenseType: "free",
            invoice: null,
            additionalInfo: "Self Registered",
          },
        ],
        odontogramServices: [],
        WAMessage: {
          message:
            "Hola%20{nombre},%20estas%20hablando%20con%20el%20{consultorio}.%20Te%20esperamos%20a%20las%20{comienza}%20en%20{direccion}",
        },
        praxisEmail: "",
      },
    };

    try {
      const registeredUser = await registerOwner(
        praxisOwner,
        formData.password,
        referal
      );

      if (registeredUser) {
        dispatch(setUserDetails(registeredUser));
        sendRegister(`Nuevo usuario Creado: ${registeredUser?.email}`);
        // Navigation will happen automatically via useEffect when userDetails updates
      }
    } catch (error) {
      console.error("Registration error:", error);
      // You might want to show an error message to the user here
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <nav className="fixed top-0 left-0 right-0 bg-white backdrop-blur-xl border-b border-slate-200 z-50 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-16">
                <img
                  src={logo}
                  className="object-contain md:h-12 h-9 w-auto"
                  alt="OdontoStark"
                />
              </div>
            </div>
          </nav>

          <h1 className="text-3xl font-bold text-slate-900 mb-2 mt-10">
            Crea tu cuenta
          </h1>
          <p className="text-slate-600">
            30 días gratis, sin tarjeta de crédito
          </p>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100/80 backdrop-blur rounded-full text-emerald-700 text-sm font-semibold mt-4 border border-emerald-200/60">
            <Sparkles className="w-4 h-4" />
            <span>Acceso completo durante la prueba</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          <div className="space-y-5">
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">
                Información Personal
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <InputField
                  label="Nombre"
                  id="name"
                  value={formData.name}
                  onChange={handleChange("name")}
                  placeholder="Juan"
                  required
                  success={formData.name.length > 0}
                />

                <InputField
                  label="Apellido"
                  id="lastName"
                  value={formData.lastName}
                  onChange={handleChange("lastName")}
                  placeholder="Pérez"
                  required
                  success={formData.lastName.length > 0}
                />
              </div>

              <InputField
                label="Email"
                id="email"
                type="email"
                value={formData.email}
                onChange={handleChange("email")}
                placeholder="tu@email.com"
                required
                error={validation.email === false}
                success={validation.email === true}
                helpText={validation.email === false ? "Email inválido" : ""}
              />

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Contraseña <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={isPasswordVisible ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange("password")}
                    placeholder="Mínimo 6 caracteres"
                    className={`block w-full px-4 py-3 pr-12 rounded-xl border-2 transition-all text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 ${
                      validation.password === false
                        ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                        : validation.password === true
                        ? "border-emerald-300 focus:border-emerald-500 focus:ring-emerald-500/20"
                        : "border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    {isPasswordVisible ? (
                      <EyeOff className="w-5 h-5 text-slate-500" />
                    ) : (
                      <Eye className="w-5 h-5 text-slate-500" />
                    )}
                  </button>
                  {validation.password === true && (
                    <CheckCircle className="absolute right-12 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
                  )}
                </div>
                {validation.password === false && (
                  <p className="mt-1 text-xs text-red-600">
                    La contraseña debe tener al menos 6 caracteres
                  </p>
                )}
              </div>
            </div>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-4 text-slate-500 font-medium">
                  Información del Consultorio
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <InputField
                label="Nombre del Consultorio"
                id="praxisName"
                value={formData.praxisName}
                onChange={handleChange("praxisName")}
                placeholder="Clínica Dental..."
                required
                success={formData.praxisName.length > 0}
              />

              <InputField
                label="Teléfono del Consultorio"
                id="praxisTel"
                type="tel"
                value={formData.praxisTel}
                onChange={handleChange("praxisTel")}
                placeholder="+595 981 123456"
                required
                error={validation.phone === false}
                success={validation.phone === true}
                helpText={
                  validation.phone === false
                    ? "Ingresa un número válido (mín. 10 dígitos)"
                    : ""
                }
              />
            </div>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canCreate || isLoading}
              className={`w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
                canCreate && !isLoading
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-lg shadow-blue-600/30 hover:shadow-xl hover:scale-105"
                  : "bg-slate-200 text-slate-400 cursor-not-allowed"
              }`}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creando cuenta...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  Comenzar prueba gratuita
                  <ArrowRight className="w-5 h-5" />
                </div>
              )}
            </button>

            <p className="text-xs text-center text-slate-500">
              Al registrarte, aceptas nuestros términos de servicio y política
              de privacidad
            </p>
          </div>
        </div>

        <div className="text-center mt-6">
          <p className="text-slate-600">
            ¿Ya tienes una cuenta?{" "}
            <Link
              to="/login"
              className="font-semibold text-blue-600 hover:text-blue-700 transition-colors"
            >
              Iniciar sesión
            </Link>
          </p>
        </div>

        <div className="mt-8 flex items-center justify-center gap-6 text-xs text-slate-500">
          <div className="flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-emerald-500" />
            <span>30 días gratis</span>
          </div>
          <div className="flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-emerald-500" />
            <span>Sin tarjeta</span>
          </div>
          <div className="flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-emerald-500" />
            <span>Cancela cuando quieras</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
